import { environment } from 'src/environments/environment';
import { IAttendanceRecord } from '../../models/academic';
import { IBankDetails } from '../../models/finance';
import { IReport } from '../../models/resource';
import {
  AccountLinks,
  IAccountHolder,
  IAddress,
  IEmergencyContact,
  IGuardian,
  ILearnerAccess,
  IPhoneNumber,
  IRelationship,
  IStudent,
} from '../../models/user';
import { LookupService } from '../../services/lookup/lookup.service';
import {
  AbsenteeRecord,
  Address,
  Allocation,
  BankAccount,
  Company,
  Content,
  Email,
  EmergencyContact,
  Guardian,
  LinkedAccount,
  MedicalAidInfo,
  PhoneNumber,
  Relationship,
  Report,
  Student,
} from '../models/gqlModels';

// Index type for account/company by studentId
export type StudentAccessIndex = Record<string, { accountCode: string; companyCode: string }>;

export function mapGraphQLGuardianToIGuardian(content: Content, lookupService: LookupService): IGuardian {
  let phoneNumbers = [...(content.person?.phoneNumbers ?? [])];
  const emailAddresses = [...(content.person?.emailAddresses ?? [])].sort((a, b) =>
    (a.address ?? '').localeCompare(b.address ?? ''),
  );

  const firstname = content.person?.firstName ?? '';
  const lastname = content.person?.lastName ?? '';
  const primaryPhone = phoneNumbers?.find((rec) =>
    rec?.phoneNumberType?.description?.toLocaleLowerCase()?.includes('mobile'),
  ); // Assuming the first mobile phone number is the primary one

  phoneNumbers = phoneNumbers.filter((rec) => rec?.id != primaryPhone?.id);

  const mappedNumbers = phoneNumbers.map((phone) => mapPhoneNumber(phone, lookupService));

  const employerNumber = phoneNumbers.find((rec) =>
    rec?.phoneNumberType?.description?.toLocaleLowerCase()?.includes('office'),
  ); // Assuming the first office phone number is the primary one

  let imageUrl = content.imageUrl
    ? content.imageUrl.includes('/ds-msal/webp')
      ? `${environment.apiBaseUrl}/ds-msal/webp` + content.imageUrl.split('/ds-msal/webp')[1]
      : `${environment.apiBaseUrl}${content.imageUrl}`
    : '';

  if (!imageUrl || imageUrl.endsWith('/null')) {
    imageUrl = '';
  }

  const homeAddress = content.person?.addresses?.filter((address) => address.addressType?.description === 'Home')?.[0];
  const addressToUse = homeAddress || content.person?.addresses?.[0];

  const learnerAccess =
    content?.students
      ?.filter((student) => {
        return student.guardianRole && student.guardianRole.toLowerCase() !== 'unassigned';
      })
      ?.map(
        (student) =>
          ({
            learnerId: student.id,
            userType: student.guardianRole,
            color: student.color,
            accountCode: student.accountCode,
          }) as ILearnerAccess,
      ) ?? [];

  return {
    id: content.id ?? '',
    personId: content.person?.id ?? '',
    title: content?.person?.title ? mapRelationship(content.person.title) : { id: '', description: '' },
    // isAccountHolder: content.accountHolder ?? false,
    pictureId: content.imageId ?? '',
    pictureUrl: imageUrl,
    learnerAccess,
    isGuardian: true,
    currentStatusCode: content.currentStatusCode ?? '',
    maritalStatus: content.maritalStatus ?? '',
    parentalStatus: content.parentalStatus ? mapRelationship(content.parentalStatus) : { id: '', description: '' },
    learnerRelationship: content.relationship ? mapRelationship(content.relationship) : null,
    idPassport: content.person?.idNumber ?? '',
    firstname: firstname,
    lastname: lastname,
    username: content.person?.nickName ?? firstname,
    email: content.person?.emailAddresses?.[0]?.address ?? '',
    primaryEmail: getPrimaryEmail(emailAddresses),
    primaryPhone: mapPhoneNumber(primaryPhone, lookupService),
    additionalNumbers: mappedNumbers,
    additionalEmails: emailAddresses?.filter((email) => email.id !== mapPhoneNumber(primaryPhone, lookupService)?.id),
    cellCode: { id: '', description: '' },
    phoneType: '',
    employment: {
      employer: content.employerName ?? '',
      occupation: content.occupation ?? '',
      employerNumber: mapPhoneNumber(employerNumber, lookupService),
    },
    address: mapAddress(addressToUse),
    accountLinks: mapAccountLinks(content.linkedAccounts ?? []),
    linkedAccountHolders: mapGraphQLGuardiansToIAccountHolders(content.linkedAccounts ?? []), // Simplified: removed to reduce unnecessary data fetching
    status: content.currentStatusCode ?? 'ACT',
    students: content.students,
  };
}

export function mapGraphQLStudentToIStudent(
  student: Student,
  lookupService: LookupService,
  currentStudentYear: Allocation | undefined,
  accessIndex?: StudentAccessIndex,
  token?: string | null,
): IStudent {
  const person = student.person;

  let imageUrl = student.imageUrl
    ? student.imageUrl.includes('/ds-msal/webp')
      ? `${environment.apiBaseUrl}/ds-msal/webp` + student.imageUrl.split('/ds-msal/webp')[1]
      : `${environment.apiBaseUrl}${student.imageUrl}`
    : '';

  if (!imageUrl || imageUrl.endsWith('/null')) {
    imageUrl = '';
  }

  // Pull account/company from access index
  const access = accessIndex?.[student.id];

  return {
    preferredName: person.nickName || person.firstName,
    id: student.id,
    studentNumber: currentStudentYear?.studentNumber ?? '',
    firstname: person.firstName,
    lastname: person.lastName,
    email: student?.email?.adUsername,
    pictureId: student.imageId,
    pictureUrl: imageUrl,
    colour: student.color,
    birthday: new Date(), // Assuming birthday is not provided in the GraphQL response
    idPassport: person.idNumber,
    grade: currentStudentYear?.studentYear?.schoolGrade?.grade?.name ?? '',
    enrolmentStatus: mapStatusCode(currentStudentYear?.currentStatusCode as string), // Assuming default value for enrolment status
    enrolmentDocuments: [],
    // Add account/company from access state
    accountCode: access?.accountCode ?? '',
    companyCode: access?.companyCode ?? '',
    schoolId: currentStudentYear?.studentYear?.registerClass?.school.id ?? '',
    registerClass: currentStudentYear?.studentYear?.registerClass?.name ?? '',
    classIds: [],
    extracurricularIds: [],
    attendance: currentStudentYear?.studentYear.absenteeRecords
      ? mapAbsenteeRecordsToIAttendanceRecords(currentStudentYear.studentYear.absenteeRecords)
      : [],
    reportCardUrls: [],
    medicalInfo: mapMedicalInfo(student.medicalAidInfo),
    emergencyContacts: student.emergencyContact ? [mapEmergencyContact(student.emergencyContact, lookupService)] : [],
    school: mapSchoolCompanySettings(student),
  };
}

export function mapGraphQLGuardianToIAccountHolder(guardian: Guardian): IAccountHolder {
  const person = guardian.person;
  let imageUrl = guardian.imageUrl?.replace(/^http:\/\/[^\/]+/, '');
  if (!imageUrl || imageUrl.endsWith('/null')) {
    imageUrl = 'assets/images/placeholder-guardian.webp';
  } else {
    // Add the environment base URL to the cleaned path
    imageUrl = `${environment.apiBaseUrl}${imageUrl}`;
  }
  const primaryPhone = guardian?.person?.phoneNumbers?.find((rec) =>
    rec?.phoneNumberType?.description?.toLocaleLowerCase()?.includes('mobile'),
  ); // Assuming the first mobile phone number is the primary one

  return {
    guadianId: guardian.id,
    accountCode: guardian.accountCode ?? '',
    companyCode: guardian.companyCode ?? '',
    firstName: person.firstName ?? '',
    lastName: person.lastName ?? '',
    title: person.title ? mapRelationship(person.title).description : '',
    email: person.emailAddresses?.[0]?.address ?? '',
    phoneNumber: primaryPhone?.number ?? '',
    pictureUrl: imageUrl,
    relationship: guardian.relationship ? mapRelationship(guardian.relationship).description : '',
  };
}

export function mapGraphQLGuardiansToIAccountHolders(linkedAccounts: LinkedAccount[]): IAccountHolder[] {
  const accountHoldersDetails: IAccountHolder[] = [];
  linkedAccounts.forEach((LinkedAccount: LinkedAccount) => {
    const person = LinkedAccount?.guardian?.person;
    let imageUrl = LinkedAccount?.guardian?.imageUrl?.replace(/^http:\/\/[^\/]+/, '');
    if (!imageUrl || imageUrl.endsWith('/null')) {
      imageUrl = 'assets/images/placeholder-guardian.webp';
    } else {
      // Add the environment base URL to the cleaned path
      imageUrl = `${environment.apiBaseUrl}${imageUrl}`;
    }
    const primaryPhone = person?.phoneNumbers?.find((rec) =>
      rec?.phoneNumberType?.description?.toLocaleLowerCase()?.includes('mobile'),
    ); // Assuming the first mobile phone number is the primary one

    const accountHolderDetails: IAccountHolder = {
      accountCode: LinkedAccount?.accountCode ?? '',
      companyCode: LinkedAccount?.company?.companyCode ?? '',
      firstName: person?.firstName ?? '',
      lastName: person?.lastName ?? '',
      title: person?.title ? mapRelationship(person?.title).description : '',
      email: person?.emailAddresses?.[0]?.address ?? '',
      phoneNumber: primaryPhone?.number ?? '',
      pictureUrl: imageUrl,
      relationship: LinkedAccount?.guardian?.relationship
        ? mapRelationship(LinkedAccount?.guardian?.relationship).description
        : '',
      companyName: LinkedAccount?.company?.myHubCompanySettings?.companyName || '',
    };

    accountHoldersDetails.push(accountHolderDetails);
  });

  return accountHoldersDetails ?? [];
}

function mapStatusCode(
  statusCode: string,
):
  | 'Active'
  | 'Review'
  | 'Application Submitted'
  | 'Cancelled'
  | 'Feedback'
  | 'Documents Submitted'
  | 'Paid'
  | 'Inactive'
  | 'Approved'
  | 'Pending' {
  switch (statusCode) {
    case 'ACT':
      return 'Active';
    case 'IAC':
      return 'Inactive'; // Mapping IAC to Inactive - adjust if needed
    case 'PED':
      return 'Pending'; // Mapping PEN to Application Submitted - adjust if needed
    case 'CAN':
      return 'Cancelled'; // Mapping CAN to Cancelled - adjust if needed
    case 'APR':
      return 'Approved';
    default:
      return 'Active'; // Default fallback - adjust if needed
  }
}

function mapMedicalInfo(medicalAidInfo: MedicalAidInfo) {
  return {
    provider: medicalAidInfo?.scheme?.id,
    mainMemberNumber: medicalAidInfo?.number,
    mainMemberName: medicalAidInfo?.principleMemberName,
    idType: medicalAidInfo?.principleMemberIdType,
    mainMemberId: medicalAidInfo?.principleMemberId,
  };
}

function mapRelationship(relationship: Relationship): IRelationship {
  return {
    id: relationship?.id ?? '',
    description: relationship?.description, // Assuming the description is the same as the ID
  };
}

function mapEmergencyContact(emergencyContact: EmergencyContact, lookupService: LookupService): IEmergencyContact {
  let email = '';
  if (emergencyContact?.person?.emailAddresses?.length > 0) {
    email = emergencyContact.person.emailAddresses[0].address;
  }
  const countryCode = '+27';
  const phoneNumber = '';
  const phoneArray = emergencyContact?.person.phoneNumbers ?? [];
  const phoneNo = mapPhoneNumber(phoneArray[0], lookupService);

  return {
    name: emergencyContact.person.firstName,
    surname: emergencyContact.person.lastName,
    relationship: mapRelationship(emergencyContact.relationship),
    countryCode: phoneNo.cellCode ?? countryCode,
    phoneNumber: phoneNo.number,
    email: email,
    emailType: emergencyContact?.person?.emailAddresses?.[0]?.emailAddressType?.id ?? null,
    phoneType: emergencyContact?.person?.phoneNumbers?.[0]?.phoneNumberType?.id,
    nationalNumber: phoneNumber,
    phoneId: phoneNo.id,
    emailId: emergencyContact?.person?.emailAddresses?.[0]?.id,
  };
}

function mapAddress(address: Address | undefined): IAddress {
  if (!address)
    return {
      id: '',
      type: { id: '', description: '' },
      streetName: '',
      unitNumber: '',
      suburb: '',
      city: '',
      region: '',
      complex: '',
      areaCode: '0000',
    };

  // Extract the number part as unit number and the rest as street name
  let unitNumber = '';
  let streetName = address.streetName || '';

  // Check if the streetName exists and starts with a number
  if (streetName) {
    const parts = streetName.match(/^(\d+)\s+(.+)$/);
    if (parts) {
      unitNumber = parts[1]; // The number part
      streetName = parts[2]; // The rest of the address
    }
  }

  return {
    id: address.id,
    type: { id: address.addressType.id, description: address.addressType.description },
    streetName,
    unitNumber,
    suburb: address.suburb,
    city: address.town,
    region: address.region,
    complex: address.complexErf,
    areaCode: /^\d{4}$/.test(address.postalCode)
      ? (address.postalCode as `${number}${number}${number}${number}`)
      : '0000',
  };
}

function mapPhoneNumber(phone: PhoneNumber | undefined, lookupService: LookupService): IPhoneNumber {
  const countries: any[] = lookupService.countries();

  if (!phone) return { id: '', number: '', cellCode: '', type: { id: '', description: '' } };

  let cellCode = phone.countryCode;

  // 1. If countryCode consists of only letters
  if (/^[a-zA-Z]+$/.test(cellCode ?? '')) {
    const match = countries.find((c) => c.code?.toLowerCase() === cellCode?.toLowerCase());
    if (match && match.dialCode) {
      cellCode = match.dialCode;
    }
  }
  // 2. If countryCode is a number and does not contain '+'
  else if (/^\d+$/.test(cellCode ?? '')) {
    const match = countries.find((c) => c.dialCode?.replace('+', '') === cellCode);
    if (match && match.dialCode) {
      cellCode = match.dialCode;
    }
  }
  // 3. If countryCode contains '+'
  else if (cellCode?.includes('+')) {
    const match = countries.find((c) => c.dialCode === cellCode);
    if (match && match.dialCode) {
      cellCode = match.dialCode;
    }
  }

  const codeLength = cellCode?.length as number;
  const number = '0' + phone.number.substring(codeLength);

  return {
    id: phone.id,
    number,
    cellCode,
    type: mapRelationship(phone.phoneNumberType),
  };
}

function mapAccountLinks(accountLinks: LinkedAccount[]): AccountLinks[] {
  return accountLinks.map((link) => ({
    accountCode: link.accountCode ?? '',
    companyCode: link.company?.companyCode ?? '',
    isAccountHolder: link.accountHolder ?? false,
  }));
}

export function mapReportsToResources(reports: Report[]): IReport[] {
  return reports.map((report) => ({
    id: report.reportRef,
    name: `Report ${report.reportYear} ${report.reportTerm}`,
    type: 'Document',
    documentUrl: `/myhub-api/academic-report-pdf/${report.reportRef}`,
    year: report.reportYear?.trim(),
    term: report.reportTerm?.trim(),
    timestamp: new Date(report.dateCreated),
  }));
}

export function mapBankAccountToIBankDetails(
  bankAccounts: BankAccount[],
  accountCode: string,
  companyCode?: string,
  companyName?: string,
): IBankDetails[] {
  return bankAccounts.map((bankAccount) => ({
    accountName: bankAccount.bankDetails.accountName ?? '',
    bankName: bankAccount.bankDetails.bankName ?? '',
    accountNumber: bankAccount.bankDetails.accountNumber ?? '',
    branchCode: bankAccount.bankDetails.branchCode ?? '',
    accountCode,
    companyName: companyName ?? '',
    companyCode: companyCode ?? '',
  }));
}

export function getPrimaryEmail(emailAddresses: Email[] | [] | null | undefined) {
  if (!emailAddresses || emailAddresses.length === 0) return undefined;
  const homeEmail = emailAddresses.find((e) => e.emailAddressType?.description?.toLowerCase() === 'home');
  return homeEmail ?? emailAddresses[0];
}

function mapSchoolTermToTerm(schoolTerm?: string): 'Term 1' | 'Term 2' | 'Term 3' | 'Term 4' {
  if (!schoolTerm) return 'Term 1';

  // Handle different possible formats
  const termLower = schoolTerm.toLowerCase();
  if (termLower.includes('1') || termLower === 'term 1') return 'Term 1';
  if (termLower.includes('2') || termLower === 'term 2') return 'Term 2';
  if (termLower.includes('3') || termLower === 'term 3') return 'Term 3';
  if (termLower.includes('4') || termLower === 'term 4') return 'Term 4';

  // Default to Term 1 if we can't parse it
  return 'Term 1';
}

export function mapAbsenteeRecordsToIAttendanceRecords(absenteeRecords: AbsenteeRecord[]): IAttendanceRecord[] {
  return absenteeRecords
    .filter((record) => {
      const shortCode = record.type?.shortCode?.toUpperCase();
      const desc = record.type?.description?.toLowerCase() ?? '';

      // Ignore Present and Remote Learning
      if (shortCode === 'PRE' || shortCode === 'RML') return false;
      if (!shortCode && (desc.includes('present') || desc.includes('remote'))) return false;

      return true;
    })
    .map((record) => {
      const shortCode = record.type?.shortCode?.toUpperCase();
      const desc = record.type?.description?.toLowerCase() ?? '';

      // Map to one of the allowed statuses
      let status: 'Absent' | 'Late' | 'Missed subjects' | 'Left early';
      if (shortCode === 'LAT' || desc.includes('late') || desc.includes('tardy')) {
        status = 'Late';
      } else if (shortCode === 'MSS' || shortCode === 'MSP' || desc.includes('missed')) {
        status = 'Missed subjects';
      } else if (shortCode === 'LFT' || desc.includes('left early') || desc.includes('early departure')) {
        status = 'Left early';
      } else if (shortCode === 'ABC' || desc.includes('covid')) {
        // Absent (COVID-19 related) maps to Absent
        status = 'Absent';
      } else {
        // Default fallback
        status = 'Absent';
      }

      const missedSubjectsAbbreviations =
        record.missedSubjects?.map((s) => s.schoolSubject?.subject?.abbreviation).filter(Boolean) || [];

      const absenteeRecord: IAttendanceRecord = {
        id: record.id,
        date: new Date(record.date),
        term: mapSchoolTermToTerm(record.schoolTerm),
        status,
        academicClassId: record.missedSubjects?.[0]?.id || '',
        reason: record.reason,
        missedSubjects: missedSubjectsAbbreviations,
      };

      return absenteeRecord;
    });
}

export function mapSchoolCompanySettings(student: Student): Company | undefined {
  const account = student?.allocations?.[0]?.account;
  if (!account) return undefined;

  return {
    accountCode: account.accountCode,
    companyCode: account.company.companyCode,
    id: account.company.id,
    bursar: account.company.bursar,
    myHubCompanySettings: account.company.myHubCompanySettings,
  };
}
